// Sidebar.js - Version Mobile Optimisée
import React, { useContext, useEffect, useState } from 'react';
import { BookingContext } from '../../context/BookingContext';
import './Sidebar.css';

const Sidebar = () => {
  const { 
    bookingData, 
    getTotalPrice, 
    getConsultationModeLabel,
    isRemoteConsultation,
    isHomeVisitConsultation,
    isCabinetConsultation
  } = useContext(BookingContext);

  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 769);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sur mobile, la sidebar n'est jamais rendue
  // Le récapitulatif est affiché dans le composant mobile dédié
  if (isMobile) {
    return null;
  }

  // Fonction pour obtenir l'icône du mode de consultation
  const getConsultationModeIcon = () => {
    if (isRemoteConsultation()) return '💻';
    if (isHomeVisitConsultation()) return '🏠';
    return '🏢';
  };

  // Fonction pour obtenir la classe CSS du mode de consultation
  const getConsultationModeClass = () => {
    if (isRemoteConsultation()) return 'remote-value';
    if (isHomeVisitConsultation()) return 'home-value';
    return 'cabinet-value';
  };

  // Calculer le prix total
  const totalPrice = getTotalPrice();

  // Vérifier si on a des données à afficher
  const hasBookingData = Boolean(
    bookingData.practitioner || 
    bookingData.serviceTitle || 
    bookingData.formattedDate || 
    bookingData.time || 
    bookingData.price !== null
  );

  return (
    <div className="booking-sidebar">
      <h3>Récapitulatif de votre réservation</h3>
      
      {!hasBookingData && (
        <div className="empty-summary">
          Votre récapitulatif s'affichera ici au fur et à mesure de votre sélection.
        </div>
      )}
      
      {/* Praticien */}
      {bookingData.practitioner && (
        <div className="summary-item" data-step="practitioner">
          <div className="summary-label">Praticien</div>
          <div className="summary-value">
            <div className="practitioner-info">
              <img 
                src={bookingData.practitioner.profileImage || '/images/default-avatar.png'} 
                alt={bookingData.practitioner.name}
                className="practitioner-avatar"
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                }}
              />
              <div className="practitioner-details">
                <div className="practitioner-name">{bookingData.practitioner.name}</div>
                {bookingData.practitioner.title && (
                  <div className="practitioner-title">{bookingData.practitioner.title}</div>
                )}
                {bookingData.practitioner.specialties && bookingData.practitioner.specialties.length > 0 && (
                  <div className="practitioner-specialties">
                    {bookingData.practitioner.specialties.slice(0, 2).join(', ')}
                    {bookingData.practitioner.specialties.length > 2 && '...'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Service */}
      {bookingData.serviceTitle && (
        <div className="summary-item" data-step="service">
          <div className="summary-label">Service</div>
          <div className="summary-value">
            <div className="service-title">{bookingData.serviceTitle}</div>
            {bookingData.duration && (
              <div className="service-duration">
                Durée : {formatDuration(bookingData.duration)}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Mode de consultation */}
      {bookingData.consultationMode && (
        <div className="summary-item" data-step="consultation-mode">
          <div className="summary-label">Type de consultation</div>
          <div className="summary-value">
            <span className={`mode-badge ${bookingData.consultationMode}`}>
              {getConsultationModeLabel(bookingData.consultationMode)}
            </span>
          </div>
        </div>
      )}
      
      {/* Date */}
      {bookingData.formattedDate && (
        <div className="summary-item" data-step="date">
          <div className="summary-label">Date</div>
          <div className="summary-value">
            <div className="appointment-date">{bookingData.formattedDate}</div>
          </div>
        </div>
      )}
      
      {/* Heure */}
      {bookingData.time && (
        <div className="summary-item" data-step="time">
          <div className="summary-label">Heure</div>
          <div className="summary-value">
            <div className="appointment-time">{bookingData.time}</div>
            {bookingData.duration && (
              <div className="appointment-end-time">
                (fin prévue : {calculateEndTime(bookingData.time, bookingData.duration)})
              </div>
            )}
          </div>
        </div>
      )}

      {/* Adresse pour les visites à domicile */}
      {isHomeVisitConsultation() && bookingData.clientAddress && (
        <div className="summary-item" data-step="address">
          <div className="summary-label">Adresse de visite</div>
          <div className="summary-value">
            <div className="client-address">
              <div className="address-main">
                {bookingData.clientAddress.street}
              </div>
              <div className="address-city">
                {bookingData.clientAddress.zipCode} {bookingData.clientAddress.city}
              </div>
              {bookingData.clientAddress.additionalInfo && (
                <div className="address-additional">
                  {bookingData.clientAddress.additionalInfo}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Prix */}
      {(bookingData.price !== null && bookingData.price !== undefined) && (
        <div className="summary-item price-section" data-step="price">
          <div className="summary-label">Prix</div>
          <div className="summary-value">
            <div className="price-breakdown">
              <div className="base-price">
                Service : {bookingData.price}€
              </div>
              
              {/* Frais de déplacement pour les visites à domicile */}
              {isHomeVisitConsultation() && bookingData.homeTransportFee > 0 && (
                <div className="transport-fee">
                  Déplacement : {bookingData.homeTransportFee}€
                </div>
              )}
              
              {/* Prix total */}
              <div className="total-price">
                <strong>Total : {totalPrice}€</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Informations et notes de service */}
      {bookingData.notes && (
        <div className="summary-item" data-step="notes">
          <div className="summary-label">Informations</div>
          <div className="summary-value">
            <div className="service-notes">
              {bookingData.notes}
            </div>
          </div>
        </div>
      )}

      {/* Messages d'information selon le mode */}
      {isRemoteConsultation() && (
        <div className="consultation-info remote-info">
          <div className="info-icon" aria-hidden="true">💻</div>
          <div className="info-text">
            <strong>Consultation à distance</strong>
            <br />
            Un lien de connexion Google Meet vous sera envoyé par email avant le rendez-vous.
          </div>
        </div>
      )}

      {isHomeVisitConsultation() && (
        <div className="consultation-info home-info">
          <div className="info-icon" aria-hidden="true">🏠</div>
          <div className="info-text">
            <strong>Visite à domicile</strong>
            <br />
            Le praticien se déplacera à l'adresse indiquée.
            {bookingData.homeTransportFee > 0 && (
              <div className="fee-info">
                Frais de déplacement inclus : {bookingData.homeTransportFee}€
              </div>
            )}
          </div>
        </div>
      )}

      {isCabinetConsultation() && bookingData.practitioner && (
        <div className="consultation-info cabinet-info">
          <div className="info-icon" aria-hidden="true">🏢</div>
          <div className="info-text">
            <strong>Consultation en cabinet</strong>
            <br />
            Rendez-vous au cabinet du praticien.
          </div>
        </div>
      )}

      {/* Informations de contact du praticien si disponibles */}
      {bookingData.practitioner && hasBookingData && (
        <div className="practitioner-contact-info">
          <div className="summary-item" data-step="contact">
            <div className="summary-label">Informations pratiques</div>
            <div className="summary-value">
              <div className="contact-details">
                {bookingData.practitioner.phone && (
                  <div className="contact-item">
                    <span className="contact-icon">📞</span>
                    <a href={`tel:${bookingData.practitioner.phone}`} className="contact-link">
                      {bookingData.practitioner.phone}
                    </a>
                  </div>
                )}
                {bookingData.practitioner.email && (
                  <div className="contact-item">
                    <span className="contact-icon">✉️</span>
                    <a href={`mailto:${bookingData.practitioner.email}`} className="contact-link">
                      Contact
                    </a>
                  </div>
                )}
                {!isHomeVisitConsultation() && bookingData.practitioner.address && (
                  <div className="contact-item">
                    <span className="contact-icon">📍</span>
                    <span className="contact-text">
                      {formatPractitionerAddress(bookingData.practitioner.address)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Fonction utilitaire pour formater la durée
const formatDuration = (duration) => {
  if (duration === 90) return '1h30';
  if (duration === 60) return '1h';
  if (duration === 45) return '45min';
  if (duration === 30) return '30min';
  return `${duration} min`;
};

// Fonction utilitaire pour calculer l'heure de fin
const calculateEndTime = (startTime, duration) => {
  if (!startTime || !duration) return '';
  
  try {
    const [hours, minutes] = startTime.split(':').map(Number);
    const startMinutes = hours * 60 + minutes;
    const endMinutes = startMinutes + duration;
    
    const endHours = Math.floor(endMinutes / 60);
    const endMins = endMinutes % 60;
    
    return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error('Erreur lors du calcul de l\'heure de fin:', error);
    return '';
  }
};

// Fonction utilitaire pour formater l'adresse du praticien
const formatPractitionerAddress = (address) => {
  if (!address) return '';
  
  let formattedAddress = '';
  if (address.street) formattedAddress += address.street;
  if (address.zipCode || address.city) {
    if (formattedAddress) formattedAddress += ', ';
    formattedAddress += `${address.zipCode || ''} ${address.city || ''}`.trim();
  }
  
  return formattedAddress || 'Adresse non spécifiée';
};

export default Sidebar;